import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useLibrary } from '../context/LibraryContext';
import { useBooks } from '../context/BooksContext';
import { IoBook, IoTrash, IoTime, IoLibrary as IoLib } from 'react-icons/io5';
import './Library.css';

export default function Library() {
  const { library, removeFromLibrary, getProgress } = useLibrary();
  const { getBookById, books } = useBooks();

  // Filtrer les livres supprimés (qui n'existent plus dans Firestore)
  const activeLibrary = library.filter(book => books.some(b => b.id === book.id));
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('library');
  const [confirmRemove, setConfirmRemove] = useState(null);

  const getProgressPercent = (book) => {
    const progress = getProgress(book.id);
    const fullBook = getBookById(book.id) || book;
    if (!fullBook.chapters || fullBook.chapters.length === 0) return 0;
    if (progress < 0) return 0;
    return Math.round(((progress + 1) / fullBook.chapters.length) * 100);
  };

  const getChapterName = (book) => {
    const progress = getProgress(book.id);
    const fullBook = getBookById(book.id) || book;
    if (!fullBook.chapters || fullBook.chapters.length === 0) return '';
    const chapter = fullBook.chapters[progress];
    return chapter ? chapter.title : '';
  };

  const inProgress = activeLibrary.filter(book => {
    const fullBook = getBookById(book.id);
    if (!fullBook || fullBook.chapters.length === 0) return false;
    const progress = getProgress(book.id);
    return progress >= 0 && progress < fullBook.chapters.length - 1;
  });

  const finished = activeLibrary.filter(book => {
    const fullBook = getBookById(book.id);
    if (!fullBook || fullBook.chapters.length === 0) return false;
    const progress = getProgress(book.id);
    return progress >= fullBook.chapters.length - 1;
  });

  return (
    <div className="page library-page">
      <div className="library-header">
        <h1 className="library-title">Ma Bibliothèque</h1>
        <p className="library-count">{activeLibrary.length} livre{activeLibrary.length > 1 ? 's' : ''}</p>
      </div>

      {/* Tabs */}
      <div className="library-tabs">
        <motion.button
          className={'library-tab ' + (activeTab === 'library' ? 'library-tab-active' : '')}
          onClick={() => setActiveTab('library')}
          whileTap={{ scale: 0.95 }}
        >
          <IoLib /> Tous ({activeLibrary.length})
        </motion.button>
        <motion.button
          className={'library-tab ' + (activeTab === 'reading' ? 'library-tab-active' : '')}
          onClick={() => setActiveTab('reading')}
          whileTap={{ scale: 0.95 }}
        >
          <IoBook /> En cours ({inProgress.length})
        </motion.button>
        <motion.button
          className={'library-tab ' + (activeTab === 'finished' ? 'library-tab-active' : '')}
          onClick={() => setActiveTab('finished')}
          whileTap={{ scale: 0.95 }}
        >
          <IoTime /> Terminés ({finished.length})
        </motion.button>
      </div>

      {/* Contenu */}
      {(() => {
        let displayBooks;
        switch (activeTab) {
          case 'reading': displayBooks = inProgress; break;
          case 'finished': displayBooks = finished; break;
          default: displayBooks = activeLibrary;
        }

        if (displayBooks.length > 0) {
          return (
            <div className="library-list">
              {displayBooks.map((book, i) => {
                const progress = getProgressPercent(book);
                const chapterName = getChapterName(book);
                const fullBook = getBookById(book.id);
                const isFinished = progress >= 100 || (fullBook && getProgress(book.id) >= fullBook.chapters.length - 1);

                return (
                  <React.Fragment key={book.id}>
                    <motion.div
                      className="library-item"
                      onClick={() => navigate('/book/' + book.id + '?from=library')}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.05 }}
                      whileHover={{ y: -2 }}
                    >
                      <div className="library-item-cover" style={{ background: (fullBook?.coverImage || book.coverImage) ? `url(${fullBook?.coverImage || book.coverImage}) center/cover no-repeat` : (fullBook?.coverGradient || book.coverGradient || 'linear-gradient(135deg, #2a2520, #1a1714)') }}>
                        {(fullBook?.coverImage || book.coverImage) ? null : (
                          isFinished ? (
                            <span style={{ fontSize: 20 }}>✓</span>
                          ) : (
                            <IoBook className="library-item-cover-icon" />
                          )
                        )}
                      </div>
                      <div className="library-item-info">
                        <h3 className="library-item-title">{book.title}</h3>
                        <p className="library-item-author">{book.author}</p>
                        {chapterName && (
                          <p className="library-item-chapter">{chapterName}</p>
                        )}
                        <div className="library-item-progress">
                          <div className="library-progress-bar">
                            <div
                              className={'library-progress-fill ' + (isFinished ? 'library-progress-finished' : '')}
                              style={{ width: progress + '%' }}
                            />
                          </div>
                          <span className={'library-progress-text ' + (isFinished ? 'library-progress-text-finished' : '')}>
                            {isFinished ? 'Terminé' : progress + '%'}
                          </span>
                        </div>
                      </div>
                      <button
                        className="library-item-remove"
                        onClick={(e) => { e.stopPropagation(); setConfirmRemove(book.id); }}
                      >
                        <IoTrash />
                      </button>
                    </motion.div>

                    <AnimatePresence>
                      {confirmRemove === book.id && (
                        <motion.div
                          className="library-remove-confirm"
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <p>Retirer <strong>« {book.title} »</strong> de ta bibliothèque ?</p>
                          <div className="library-remove-confirm-actions">
                            <button className="btn btn-ghost" style={{ fontSize: 12 }} onClick={() => setConfirmRemove(null)}>Annuler</button>
                            <button className="btn library-remove-confirm-btn" style={{ fontSize: 12 }} onClick={() => { removeFromLibrary(book.id); setConfirmRemove(null); }}>Oui, retirer</button>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </React.Fragment>
                );
              })}
            </div>
          );
        }

        return (
          <div className="empty-state">
            <div className="empty-state-icon">
              {activeTab === 'finished' ? '🏆' : activeTab === 'reading' ? '📖' : '📚'}
            </div>
            <p className="empty-state-text">
              {activeTab === 'finished'
                ? "Tu n'as pas encore terminé de livre. Continue ta lecture !"
                : activeTab === 'reading'
                ? "Aucun livre en cours. Swipe pour en découvrir !"
                : "Ta bibliothèque est vide ! Swipe vers la droite pour ajouter des livres."}
            </p>
            {activeTab === 'library' && (
              <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={() => navigate('/')}>
                Découvrir des livres
              </button>
            )}
          </div>
        );
      })()}
    </div>
  );
}